import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Storefront, HandHeart, MapPin, FunnelSimple } from "@phosphor-icons/react";

/**
 * Grants.gov eligibility codes (from the Search2 API). Used by the quick-filter
 * chips so users can scope to SBA, nonprofit, or state-targeted opportunities
 * with a single click. Reference:
 * https://grants.gov/web/grants/applicants/applicant-eligibility.html
 */
export const PRESET_FILTERS = {
  all: { label: "All grants", chips: {} },
  sba: {
    label: "Small Business (SBA)",
    chips: { agencies: ["SBA"], eligibilities: ["23"] },
  },
  nonprofit: {
    label: "Nonprofits",
    // 12 = 501(c)(3); 13 = non-501(c)(3) nonprofits.
    chips: { eligibilities: ["12", "13"] },
  },
  state: {
    // 00 = state govts, 01 = county, 02 = city/township.
    label: "State-specific",
    chips: { eligibilities: ["00", "01", "02"] },
  },
};

export const US_STATES = [
  ["AL","Alabama"],["AK","Alaska"],["AZ","Arizona"],["AR","Arkansas"],["CA","California"],
  ["CO","Colorado"],["CT","Connecticut"],["DE","Delaware"],["FL","Florida"],["GA","Georgia"],
  ["HI","Hawaii"],["ID","Idaho"],["IL","Illinois"],["IN","Indiana"],["IA","Iowa"],
  ["KS","Kansas"],["KY","Kentucky"],["LA","Louisiana"],["ME","Maine"],["MD","Maryland"],
  ["MA","Massachusetts"],["MI","Michigan"],["MN","Minnesota"],["MS","Mississippi"],["MO","Missouri"],
  ["MT","Montana"],["NE","Nebraska"],["NV","Nevada"],["NH","New Hampshire"],["NJ","New Jersey"],
  ["NM","New Mexico"],["NY","New York"],["NC","North Carolina"],["ND","North Dakota"],["OH","Ohio"],
  ["OK","Oklahoma"],["OR","Oregon"],["PA","Pennsylvania"],["RI","Rhode Island"],["SC","South Carolina"],
  ["SD","South Dakota"],["TN","Tennessee"],["TX","Texas"],["UT","Utah"],["VT","Vermont"],
  ["VA","Virginia"],["WA","Washington"],["WV","West Virginia"],["WI","Wisconsin"],["WY","Wyoming"],
  ["DC","District of Columbia"],["PR","Puerto Rico"],
];

const CHIP_ICONS = {
  all: FunnelSimple,
  sba: Storefront,
  nonprofit: HandHeart,
  state: MapPin,
};

export default function PresetChips({ preset, onPreset, stateCode, onStateChange }) {
  return (
    <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="preset-chips">
      <span className="gf-overline mr-1">Quick filter</span>
      {Object.entries(PRESET_FILTERS).map(([key, def]) => {
        const Icon = CHIP_ICONS[key];
        const active = preset === key;
        return (
          <Button
            key={key}
            type="button"
            onClick={() => onPreset(key)}
            data-testid={`preset-chip-${key}`}
            className={`gf-sharp h-9 px-3 font-mono text-xs uppercase tracking-wide ${
              active
                ? "bg-blue-700 hover:bg-blue-800 text-white"
                : "bg-white border border-zinc-300 text-zinc-700 hover:bg-zinc-50"
            }`}
          >
            {Icon && <Icon size={14} weight={active ? "bold" : "regular"} className="mr-1.5" />}
            {def.label}
          </Button>
        );
      })}
      {preset === "state" && (
        <div className="ml-2" data-testid="preset-state-select">
          <Select value={stateCode || "all"} onValueChange={onStateChange}>
            <SelectTrigger className="gf-sharp h-9 border-zinc-300 bg-white text-xs font-mono uppercase tracking-wide w-[200px]">
              <SelectValue placeholder="Pick a state" />
            </SelectTrigger>
            <SelectContent className="gf-sharp max-h-[320px]">
              <SelectItem value="all">All states</SelectItem>
              {US_STATES.map(([code, name]) => (
                <SelectItem key={code} value={code}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}
